import {
  KeyboardEvent,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Box,
  InputLabel,
  TextField,
  Button,
  Stack,
} from '@mui/material';

import { VehicleApi } from '@/api';
import { IcArrowDown } from '@/assets/images';
import { AutoCompleteRoot } from '@/components/FormElements/Autocomplete/styles';
import { IVehicleModel } from '@/models';
import { useToastStore } from '@/store';

interface IVehicleModelAutoCompleteProps {
  makeId?: string;
  makeName?: string; // Added to support custom make names
  year?: number;
  excludeVehicleModels?: Array<IVehicleModel>;
  noResults?: ReactNode;
  disabled?: boolean;
  label: string;
  value?: string; // Made optional
  onChange: (value: { name: string; _id?: string }) => void;
  onKeyUp?: (e: KeyboardEvent) => void;
  sx?: any;
  vehicleType?: string;
}

export const VehicleModelAutocomplete = ({
  makeId,
  makeName,
  year,
  excludeVehicleModels = [],
  noResults,
  onChange,
  value,
  label,
  onKeyUp,
  sx,
  disabled = false,
  vehicleType,
  ...restProps
}: IVehicleModelAutoCompleteProps) => {
  const [searchText, setSearchText] = useState(String(value || ''));
  const [vehicleModels, setVehicleModels] = useState<Array<IVehicleModel>>([]);
  const [selectedModel, setSelectedModel] = useState<IVehicleModel | null>(
    null,
  );
  const [canUseCustomInput, setCanUseCustomInput] = useState(false);

  const { updateToast } = useToastStore();

  // Update searchText when value prop changes
  useEffect(() => {
    const newValue = String(value || '');
    if (searchText !== newValue) {
      setSearchText(newValue);
    }
  }, [value]);

  useEffect(() => {
    // Determine if we can use custom input
    // Can use custom if: we have a make (either API makeId OR custom makeName)
    const hasValidMake = Boolean(makeId || makeName);

    setCanUseCustomInput(hasValidMake);

    // Only fetch API models if we have a valid API makeId and year
    if (makeId && year) {
      fetchVehicleModels();
    } else {
      setVehicleModels([]); // Clear API results for custom makes or when no year
    }
  }, [makeId, makeName, year]);

  const fetchVehicleModels = async () => {
    if (!makeId || !year) return;

    try {
      const res = await VehicleApi.getVehicleModels(makeId, year, vehicleType);
      setVehicleModels(res.data);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleInputChange = (_: SyntheticEvent, v: string) => {
    setSearchText(v);
  };

  const handleCreateCustomModel = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!searchText || !canUseCustomInput) return;

    const newModel: IVehicleModel = {
      _id: `custom-${Date.now()}`,
      name: searchText,
      make: makeId || makeName || '',
      years: [],
    };

    setVehicleModels((prev) => [...prev, newModel]);
    setSelectedModel(newModel);
    onChange({ name: searchText }); // Let parent handle the actual creation
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchText && canUseCustomInput) {
      e.preventDefault();

      // Check if the current input matches an API option
      const existingModel = vehicleModels.find(
        (model) => model.name.toLowerCase() === searchText.toLowerCase(),
      );

      if (existingModel) {
        // Use the API option
        setSelectedModel(existingModel);
        onChange({ name: existingModel.name, _id: existingModel._id });
      } else {
        // Create custom value - let parent component handle the creation
        handleCreateCustomModel();
      }
    }
  };

  const getDisplayValue = () => {
    if (selectedModel) {
      return selectedModel;
    }
    // For custom string values, create a temporary object for display
    if (searchText) {
      return { name: searchText, _id: '', make: makeId || '', years: [] };
    }
    return null;
  };

  const showCreateOption =
    canUseCustomInput &&
    searchText.trim() &&
    !vehicleModels.some(
      (model) => model.name.toLowerCase() === searchText.toLowerCase(),
    );

  const getDisabledMessage = () => {
    if (!makeName && !makeId) {
      return 'Please select a make first';
    }
    return '';
  };

  const isFieldDisabled = disabled || !canUseCustomInput;

  return (
    <AutoCompleteRoot sx={sx} {...restProps}>
      {!!label && <InputLabel>{label}</InputLabel>}

      <Autocomplete
        inputValue={searchText}
        value={getDisplayValue()}
        autoHighlight
        popupIcon={<IcArrowDown />}
        fullWidth
        disabled={isFieldDisabled}
        onInputChange={handleInputChange}
        options={vehicleModels}
        freeSolo={true} // Allow free text input
        noOptionsText={
          !canUseCustomInput ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              {getDisabledMessage()}
            </Box>
          ) : (
            noResults
          )
        }
        filterOptions={(allVehicleModels) => {
          const filteredByExcluded = allVehicleModels.reduce((acc, item) => {
            const found = (excludeVehicleModels || []).find((excludedItem) => {
              return excludedItem?._id === item._id;
            });
            return found ? acc : [...acc, item];
          }, [] as Array<IVehicleModel>);

          // Add create option if search text doesn't match any existing option
          if (showCreateOption) {
            filteredByExcluded.push({
              _id: 'create-new',
              name: `+ Create "${searchText}" as a new item`,
              make: makeId || '',
              years: [],
            } as IVehicleModel);
          }

          return filteredByExcluded;
        }}
        onChange={(_, selectedOption) => {
          if (selectedOption) {
            if (typeof selectedOption === 'string') {
              // Free text input
              setSelectedModel(null);
              setSearchText(selectedOption);
              onChange({ name: selectedOption });
            } else if (selectedOption._id === 'create-new') {
              // Create new option selected - use searchText, not the display name
              setSelectedModel(null);
              onChange({ name: searchText }); // Use searchText instead of selectedOption.name
            } else {
              // API option selected
              setSelectedModel(selectedOption);
              setSearchText(selectedOption.name);
              onChange({ name: selectedOption.name, _id: selectedOption._id });
            }
          }
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.name;
        }}
        isOptionEqualToValue={(option, selectedValue) => {
          if (typeof option === 'string' && typeof selectedValue === 'string') {
            return option === selectedValue;
          }
          if (typeof option === 'object' && typeof selectedValue === 'object') {
            return option._id === selectedValue._id;
          }
          return false;
        }}
        renderOption={(props, option) => {
          if (option._id === 'create-new') {
            return (
              <Stack
                component="li"
                {...props}
                key={option._id}
                justifyContent="flex-end"
                width="100%"
                sx={{ m: 1 }}
              >
                <Button
                  onClick={handleCreateCustomModel}
                  variant="text"
                  color="error"
                  sx={{ justifyContent: 'flex-start', width: '100%' }}
                >
                  {option.name}
                </Button>
              </Stack>
            );
          }

          return (
            <Box
              component="li"
              {...props}
              key={option._id}
              flexDirection="column"
              flexWrap="wrap"
              sx={{ m: 1 }}
            >
              <p style={{ width: '100%', margin: 0 }}>{option.name}</p>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            value={searchText}
            placeholder={isFieldDisabled ? getDisabledMessage() : 'Model'}
            onKeyUp={(e) => {
              if (onKeyUp) {
                onKeyUp(e);
              }
            }}
            onKeyDown={handleKeyDown}
            fullWidth
          />
        )}
        clearOnBlur={false}
        clearIcon={<ClearIcon sx={{ width: 20, height: 20 }} />}
        disableClearable={false}
      />
    </AutoCompleteRoot>
  );
};
